import React, { useEffect, useState, Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";

export interface TimePeriodOption {
  id: string | number;
  text?: string;
  value: string;
  subText?: string;
}

export interface SelectMenuProps {
  options: TimePeriodOption[];
  selectedOption: TimePeriodOption;
  onChange: (option: TimePeriodOption) => void;
  placeholder?: string;
  size?: "md" | "sm" | "xs";
  className?: string;
}

export function SelectMenu({
  options,
  selectedOption,
  onChange,
  placeholder = "Select an option",
  size = "md",
  className = "",
}: SelectMenuProps) {
  const [selected, _setSelected] = useState(selectedOption);

  useEffect(() => {
    _setSelected(selectedOption);
  }, [selectedOption]);

  const setSelected = (option: TimePeriodOption) => {
    onChange(option);
    _setSelected(option);
  };

  return (
    <Listbox value={selected} onChange={setSelected}>
      {({ open }) => (
        <div className={`relative ${className}`}>
          <Listbox.Button
            className={`
              relative w-full cursor-pointer rounded-md border border-bg3 bg-bg-elevated
              text-left shadow-sm focus:outline-none focus:border-prime focus:ring-2 focus:ring-prime/10
              flex flex-nowrap items-center transition-colors
              hover:border-fg2
              ${size === "md" ? "pl-3 pr-2 py-2 text-sm" : size === "sm" ? "h-8 pl-3 pr-2 text-[13px]" : "pl-2 pr-1.5 py-[3px] text-xs"}
            `}
          >
            <span className="flex-1 min-w-0 truncate pr-2 text-fg">
              {selected ? (
                <>
                  <span className="truncate">{selected.text || selected.value}</span>
                  {selected.subText && (
                    <span className="ml-2 truncate text-fg2">{selected.subText}</span>
                  )}
                </>
              ) : (
                <span className="text-fg2">{placeholder}</span>
              )}
            </span>
            <span className="pointer-events-none flex-shrink-0 flex items-center">
              <ChevronDown className="text-fg" size={size === "md" ? 16 : 14} />
            </span>
          </Listbox.Button>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className={`absolute left-0 z-10 mt-1 max-h-60 min-w-full max-w-[600px] overflow-auto rounded-md bg-bg-elevated p-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${size === "xs" ? "text-xs" : size === "sm" ? "text-[13px]" : "text-base sm:text-sm"}`}>
              {options.map((option) => (
                <Listbox.Option
                  key={option.id}
                  className={({ active }) => `
                    relative cursor-pointer select-none whitespace-nowrap transition-colors rounded-md
                    ${size === "xs" ? "py-1.5 pl-6 pr-3" : size === "sm" ? "py-1.5 pl-7 pr-3" : "py-2 pl-8 pr-4"}
                    ${active ? "bg-bg2" : ""}
                  `}
                  value={option}
                >
                  {({ selected: isSelected }) => (
                    <>
                      <span
                        className={`block truncate text-fg ${isSelected ? "font-semibold" : "font-normal"}`}
                      >
                        {option.text || option.value}
                        {option.subText && (
                          <span className="ml-2 truncate text-fg2">{option.subText}</span>
                        )}
                      </span>
                      {isSelected && (
                        <span className={`absolute inset-y-0 left-0 flex items-center ${size === "xs" ? "pl-1" : size === "sm" ? "pl-1.5" : "pl-2"}`}>
                          <Check className="text-fg" size={size === "md" ? 16 : 14} />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
